import API_BASE from '../../utils/api';
import React, { useState, useEffect } from "react";
import axios from "axios";
import Sidebar from "../../components/Sidebar";
import ProfileDropdown from "../../components/ProfileDropdown";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";

const TABS = [
  { id: "school", label: "School Info", icon: "🏫" },
  { id: "academic", label: "Academic Year", icon: "📅" },
  { id: "timings", label: "School Timings", icon: "⏰" },
  { id: "attendance", label: "Attendance", icon: "📋" },
  { id: "grading", label: "Grading System", icon: "📝" },
  { id: "fees", label: "Fee Settings", icon: "💰" },
  { id: "leaves", label: "Leave Policy", icon: "🏖️" },
  { id: "notifications", label: "Notifications", icon: "🔔" },
  { id: "uploads", label: "Image Uploads", icon: "🖼️", superAdminOnly: true },
  { id: "theme", label: "Theme & Colors", icon: "🎨" },
];

const IMAGE_TYPE_OPTIONS = ["jpg", "jpeg", "png", "gif"];
const IMAGE_SIZE_OPTIONS = [1, 2, 3, 4, 5];
const MIME_EXT_MAP = {
  "image/jpeg": ["jpg", "jpeg"],
  "image/png": ["png"],
  "image/gif": ["gif"],
};

const AdminSettings = () => {
  const { user } = useAuth();
  const { theme: currentTheme, updateTheme, THEME_PRESETS } = useTheme();
  const [activeTab, setActiveTab] = useState("school");
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_BASE}/settings`);
      setSettings(res.data);
    } catch (err) {
      console.error("Error fetching settings:", err);
      setMessage({ type: "error", text: "Failed to load settings" });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await axios.put(`${API_BASE}/settings`, settings);
      setSettings(res.data.config);
      setMessage({ type: "success", text: "Settings saved successfully!" });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.error || "Failed to save settings" });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const detectCurrentLocation = () => {
    if (!navigator.geolocation) {
      setMessage({ type: "error", text: "Geolocation not supported by your browser" });
      return;
    }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setSettings((prev) => ({
          ...prev,
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }));
        setDetecting(false);
        setMessage({ type: "success", text: "Location detected! Adjust if needed, then save." });
      },
      () => {
        setDetecting(false);
        setMessage({ type: "error", text: "Failed to detect location. Enter manually." });
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const updateGrade = (index, field, value) => {
    const updated = [...(settings.gradingSystem || [])];
    updated[index] = { ...updated[index], [field]: field === "grade" ? value : Number(value) };
    updateField("gradingSystem", updated);
  };

  const addGrade = () => {
    const grades = settings.gradingSystem || [];
    updateField("gradingSystem", [...grades, { grade: "", minMarks: 0, maxMarks: 0 }]);
  };

  const removeGrade = (index) => {
    updateField("gradingSystem", settings.gradingSystem.filter((_, i) => i !== index));
  };

  if (loading || !settings) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  const allowedImageTypes = settings.allowedImageTypes?.length ? settings.allowedImageTypes : IMAGE_TYPE_OPTIONS;
  const maxImageSizeMB = settings.maxImageSizeMB || 2;

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const exts = MIME_EXT_MAP[file.type];
    const isAllowed = exts?.some((ext) => allowedImageTypes.includes(ext));
    if (!isAllowed) {
      setMessage({ type: "error", text: `Only ${allowedImageTypes.join(", ").toUpperCase()} images are allowed` });
      e.target.value = "";
      return;
    }
    if (file.size > maxImageSizeMB * 1024 * 1024) {
      setMessage({ type: "error", text: `Image must be ${maxImageSizeMB}MB or smaller` });
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleUploadLogo = async (e) => {
    e.preventDefault();
    const file = e.target.querySelector('input[type="file"]').files[0];
    if (!file) {
      setMessage({ type: "error", text: "Please select an image" });
      return;
    }
    setUploadingLogo(true);
    setMessage(null);
    try {
      const formData = new FormData();
      formData.append("logo", file);
      const res = await axios.post(`${API_BASE}/settings/logo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSettings((prev) => ({ ...prev, logo: res.data.logo }));
      setLogoPreview(null);
      setMessage({ type: "success", text: "School logo updated! It will now show for all staff and parents." });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.error || "Failed to upload logo" });
    } finally {
      setUploadingLogo(false);
    }
  };

  const renderSchoolInfo = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">School Logo</h3>
      <p className="text-gray-500 text-sm">Shown on the login page and in the sidebar for every staff member and parent.</p>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <form onSubmit={handleUploadLogo} className="flex items-end gap-4">
          <div className="shrink-0">
            {logoPreview ? (
              <img src={logoPreview} alt="Logo Preview" className="w-16 h-16 rounded-full object-cover border-2 border-indigo-600" />
            ) : settings.logo ? (
              <img src={settings.logo} alt="School Logo" className="w-16 h-16 rounded-full object-cover border-2 border-indigo-600" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center text-2xl">🏫</div>
            )}
          </div>
          <div className="flex-1">
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif"
              onChange={handleLogoChange}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
            />
            <p className="text-xs text-gray-500 mt-1">Max {maxImageSizeMB}MB. Supported: {allowedImageTypes.join(", ").toUpperCase()}</p>
          </div>
          <button
            type="submit"
            disabled={uploadingLogo || !logoPreview}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition text-sm disabled:opacity-50 shrink-0"
          >
            {uploadingLogo ? "Uploading..." : "Upload Logo"}
          </button>
        </form>
      </div>

      <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mt-6">School Information</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField label="School Name" value={settings.schoolName} onChange={(v) => updateField("schoolName", v)} placeholder="Enter school name" />
        <InputField label="Principal Name" value={settings.principalName} onChange={(v) => updateField("principalName", v)} placeholder="Enter principal name" />
        <InputField label="Phone Number" value={settings.phone} onChange={(v) => updateField("phone", v)} placeholder="Enter phone number" />
        <InputField label="Email Address" value={settings.email} onChange={(v) => updateField("email", v)} placeholder="Enter email address" type="email" />
        <InputField label="Established Year" value={settings.establishedYear} onChange={(v) => updateField("establishedYear", v)} placeholder="e.g. 1995" />
        <div className="md:col-span-2">
          <InputField label="Address" value={settings.address} onChange={(v) => updateField("address", v)} placeholder="Enter school address" />
        </div>
      </div>

      <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mt-6">Geofencing / Location</h3>
      <p className="text-gray-500 text-sm">Set GPS coordinates for teacher selfie attendance geofencing.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <InputField label="Latitude" value={settings.latitude} onChange={(v) => updateField("latitude", v)} type="number" step="any" placeholder="e.g. 17.3850" />
        <InputField label="Longitude" value={settings.longitude} onChange={(v) => updateField("longitude", v)} type="number" step="any" placeholder="e.g. 78.4867" />
        <InputField label="Radius (meters)" value={settings.radiusInMeters} onChange={(v) => updateField("radiusInMeters", Number(v))} type="number" min="50" max="5000" placeholder="200" />
      </div>
      <button type="button" onClick={detectCurrentLocation} disabled={detecting}
        className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition text-sm">
        {detecting ? "Detecting..." : "📍 Use Current Location"}
      </button>
    </div>
  );

  const renderAcademicYear = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Academic Year Configuration</h3>
      <p className="text-gray-500 text-sm">Set the current academic year and its duration.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <InputField label="Academic Year" value={settings.academicYear} onChange={(v) => updateField("academicYear", v)} placeholder="e.g. 2025-2026" />
        <InputField label="Start Date" value={settings.academicYearStart ? settings.academicYearStart.substring(0, 10) : ""} onChange={(v) => updateField("academicYearStart", v)} type="date" />
        <InputField label="End Date" value={settings.academicYearEnd ? settings.academicYearEnd.substring(0, 10) : ""} onChange={(v) => updateField("academicYearEnd", v)} type="date" />
      </div>
    </div>
  );

  const renderTimings = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">School Timings</h3>
      <p className="text-gray-500 text-sm">Configure daily school schedule and late arrival threshold.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField label="Assembly Time" value={settings.assemblyTime} onChange={(v) => updateField("assemblyTime", v)} type="time" />
        <InputField label="School Start Time" value={settings.schoolStartTime} onChange={(v) => updateField("schoolStartTime", v)} type="time" />
        <InputField label="School End Time" value={settings.schoolEndTime} onChange={(v) => updateField("schoolEndTime", v)} type="time" />
        <InputField label="Late Arrival After" value={settings.lateArrivalTime} onChange={(v) => updateField("lateArrivalTime", v)} type="time" />
      </div>
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-700">
        Teachers arriving after the "Late Arrival" time will be marked as late in attendance.
      </div>
    </div>
  );

  const renderAttendance = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Attendance Settings</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField label="Minimum Attendance %" value={settings.minAttendancePercent} onChange={(v) => updateField("minAttendancePercent", Number(v))} type="number" min="0" max="100" />
        <InputField label="Auto-mark Absent After" value={settings.autoAbsentAfterTime} onChange={(v) => updateField("autoAbsentAfterTime", v)} type="time" />
      </div>
      <div className="space-y-3 mt-2">
        <ToggleField label="Selfie Attendance for Teachers" description="Require teachers to take a selfie for attendance verification" checked={settings.selfieAttendanceEnabled} onChange={(v) => updateField("selfieAttendanceEnabled", v)} />
        <ToggleField label="Geofencing Enabled" description="Restrict teacher attendance to within school radius" checked={settings.geofencingEnabled} onChange={(v) => updateField("geofencingEnabled", v)} />
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
        Students below {settings.minAttendancePercent}% attendance will be flagged in reports.
      </div>
    </div>
  );

  const renderGrading = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Grading System</h3>
      <div className="mb-4">
        <InputField label="Pass Percentage" value={settings.passPercentage} onChange={(v) => updateField("passPercentage", Number(v))} type="number" min="0" max="100" />
      </div>
      <div className="space-y-2">
        <div className="grid grid-cols-4 gap-2 text-sm font-medium text-gray-600 px-1">
          <span>Grade</span><span>Min Marks</span><span>Max Marks</span><span></span>
        </div>
        {(settings.gradingSystem || []).map((g, i) => (
          <div key={i} className="grid grid-cols-4 gap-2 items-center">
            <input value={g.grade} onChange={(e) => updateGrade(i, "grade", e.target.value)} className="border rounded-md px-3 py-2 text-sm" placeholder="Grade" />
            <input type="number" value={g.minMarks} onChange={(e) => updateGrade(i, "minMarks", e.target.value)} className="border rounded-md px-3 py-2 text-sm" />
            <input type="number" value={g.maxMarks} onChange={(e) => updateGrade(i, "maxMarks", e.target.value)} className="border rounded-md px-3 py-2 text-sm" />
            <button onClick={() => removeGrade(i)} className="text-red-500 hover:text-red-700 text-sm font-medium">Remove</button>
          </div>
        ))}
        <button onClick={addGrade} className="text-indigo-600 hover:text-indigo-800 text-sm font-medium mt-2">+ Add Grade</button>
      </div>
    </div>
  );

  const renderFees = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Fee Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <InputField label="Reminder Days Before Due" value={settings.feeReminderDaysBefore} onChange={(v) => updateField("feeReminderDaysBefore", Number(v))} type="number" min="1" max="30" />
        </div>
        <div className="space-y-3 mt-2">
          <ToggleField label="SMS Fee Reminders" description="Send SMS reminders to parents for pending fees" checked={settings.smsRemindersEnabled} onChange={(v) => updateField("smsRemindersEnabled", v)} />
          <ToggleField label="Email Fee Reminders" description="Send email reminders to parents for pending fees" checked={settings.emailRemindersEnabled} onChange={(v) => updateField("emailRemindersEnabled", v)} />
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Late Fee Rule</h3>
        <ToggleField label="Late Fees Enabled" description="Automatically compute and apply a late fee to overdue installments" checked={settings.lateFeeEnabled} onChange={(v) => updateField("lateFeeEnabled", v)} />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Calculation Method</label>
            <select
              value={settings.lateFeeCalcMethod || "per_day"}
              onChange={(e) => updateField("lateFeeCalcMethod", e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="per_day">Per Day</option>
              <option value="per_month">Per Month</option>
              <option value="flat">Flat (one-time)</option>
            </select>
          </div>
          <InputField
            label={settings.lateFeeCalcMethod === "flat" ? "Flat Amount (Rs.)" : `Amount per ${settings.lateFeeCalcMethod === "per_month" ? "month" : "day"} (Rs.)`}
            value={settings.lateFeeAmount}
            onChange={(v) => updateField("lateFeeAmount", Number(v))}
            type="number"
            min="0"
          />
          <InputField label="Grace Period (days)" value={settings.lateFeeGraceDays} onChange={(v) => updateField("lateFeeGraceDays", Number(v))} type="number" min="0" max="60" />
          <InputField label="Cap Amount (Rs., 0 = uncapped)" value={settings.lateFeeCapAmount} onChange={(v) => updateField("lateFeeCapAmount", Number(v))} type="number" min="0" />
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Fee Groups</h3>
        <p className="text-xs text-gray-500">
          One group per student (e.g. Day Scholar, Hostel, NRI, Transport Zone A). Used to scope fee structures and filter the fees list.
        </p>
        <ListEditor
          items={settings.feeGroups || []}
          onChange={(items) => updateField("feeGroups", items)}
          placeholder="e.g. Day Scholar"
        />
      </div>
    </div>
  );

  const renderLeaves = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Teacher Leave Policy</h3>
      <p className="text-gray-500 text-sm">Set the maximum number of leaves allowed per teacher per academic year.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <InputField label="Casual Leaves / Year" value={settings.maxCasualLeaves} onChange={(v) => updateField("maxCasualLeaves", Number(v))} type="number" min="0" />
        <InputField label="Sick Leaves / Year" value={settings.maxSickLeaves} onChange={(v) => updateField("maxSickLeaves", Number(v))} type="number" min="0" />
        <InputField label="Earned Leaves / Year" value={settings.maxEarnedLeaves} onChange={(v) => updateField("maxEarnedLeaves", Number(v))} type="number" min="0" />
      </div>
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm text-indigo-700">
        Total: {(settings.maxCasualLeaves || 0) + (settings.maxSickLeaves || 0) + (settings.maxEarnedLeaves || 0)} leaves per year per teacher
      </div>
    </div>
  );

  const renderNotifications = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Notification Preferences</h3>
      <p className="text-gray-500 text-sm">Control how notifications are sent across the system.</p>
      <div className="space-y-3">
        <ToggleField label="SMS Notifications" description="Send SMS alerts for attendance, fees, and events" checked={settings.smsNotificationsEnabled} onChange={(v) => updateField("smsNotificationsEnabled", v)} />
        <ToggleField label="Email Notifications" description="Send email alerts for attendance, fees, and events" checked={settings.emailNotificationsEnabled} onChange={(v) => updateField("emailNotificationsEnabled", v)} />
        <ToggleField label="In-App Notifications" description="Show notifications inside the app for all users" checked={settings.inAppNotificationsEnabled} onChange={(v) => updateField("inAppNotificationsEnabled", v)} />
        <ToggleField label="Parent Notifications" description="Send attendance and fee updates to parents automatically" checked={settings.parentNotificationsEnabled} onChange={(v) => updateField("parentNotificationsEnabled", v)} />
      </div>
    </div>
  );

  const toggleImageType = (ext) => {
    const current = settings.allowedImageTypes || [];
    const updated = current.includes(ext) ? current.filter((t) => t !== ext) : [...current, ext];
    updateField("allowedImageTypes", updated);
  };

  const renderUploads = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Profile Image Upload Policy</h3>
      <p className="text-gray-500 text-sm">Control which image types and maximum file size are accepted for profile picture uploads across the app. Only super admins can change this.</p>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Allowed Image Types</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {IMAGE_TYPE_OPTIONS.map((ext) => {
            const enabled = (settings.allowedImageTypes || []).includes(ext);
            return (
              <button
                key={ext}
                type="button"
                onClick={() => toggleImageType(ext)}
                className={`px-3 py-3 rounded-lg border-2 text-sm font-medium transition flex items-center gap-2 justify-center ${
                  enabled ? "bg-green-50 border-green-400 text-green-800" : "bg-gray-50 border-gray-200 text-gray-400"
                }`}
              >
                <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${enabled ? "bg-green-500 border-green-500" : "border-gray-300"}`}>
                  {enabled && <span className="text-white text-xs">✓</span>}
                </span>
                {ext.toUpperCase()}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Maximum File Size</label>
        <select
          value={settings.maxImageSizeMB || 2}
          onChange={(e) => updateField("maxImageSizeMB", Number(e.target.value))}
          className="w-full max-w-xs border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        >
          {IMAGE_SIZE_OPTIONS.map((mb) => (
            <option key={mb} value={mb}>{mb} MB</option>
          ))}
        </select>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
        Currently accepting: {(settings.allowedImageTypes || []).join(", ").toUpperCase() || "none"}, up to {settings.maxImageSizeMB || 2}MB per image.
      </div>
    </div>
  );

  const applyPreset = (presetName) => {
    const preset = THEME_PRESETS[presetName];
    if (preset) {
      setSettings((prev) => ({
        ...prev,
        themePreset: presetName,
        sidebarColor: preset.sidebarColor,
        primaryColor: preset.primaryColor,
        accentColor: preset.accentColor,
        loginBgFrom: preset.loginBgFrom,
        loginBgTo: preset.loginBgTo,
      }));
      updateTheme({ themePreset: presetName, ...preset });
    }
  };

  const handleColorChange = (field, value) => {
    updateField(field, value);
    const newTheme = {
      ...currentTheme,
      themePreset: 'custom',
      [field]: value,
    };
    setSettings((prev) => ({ ...prev, themePreset: 'custom', [field]: value }));
    updateTheme(newTheme);
  };

  const renderTheme = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Theme & Color Customization</h3>
      <p className="text-gray-500 text-sm">Choose a theme preset or customize individual colors. Changes preview instantly.</p>

      {/* Presets */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Theme Presets</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {Object.entries(THEME_PRESETS).map(([name, preset]) => (
            <button key={name} onClick={() => applyPreset(name)}
              className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition hover:shadow-md ${
                (settings.themePreset || 'indigo') === name ? 'border-gray-800 shadow-md bg-gray-50' : 'border-gray-200 hover:border-gray-400'
              }`}>
              <div className="flex gap-1">
                <div className="w-6 h-6 rounded-full border border-gray-300" style={{ backgroundColor: preset.sidebarColor }} />
                <div className="w-6 h-6 rounded-full border border-gray-300" style={{ backgroundColor: preset.primaryColor }} />
                <div className="w-6 h-6 rounded-full border border-gray-300" style={{ backgroundColor: preset.accentColor }} />
              </div>
              <span className="text-xs font-medium text-gray-700 capitalize">{name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Custom Colors */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Custom Colors</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { field: 'sidebarColor', label: 'Sidebar Color' },
            { field: 'primaryColor', label: 'Primary / Buttons' },
            { field: 'accentColor', label: 'Accent / Highlights' },
            { field: 'loginBgFrom', label: 'Login Background (From)' },
            { field: 'loginBgTo', label: 'Login Background (To)' },
          ].map(({ field, label }) => (
            <div key={field} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
              <input type="color" value={settings[field] || '#4f46e5'} onChange={(e) => handleColorChange(field, e.target.value)}
                className="w-10 h-10 rounded cursor-pointer border-0 p-0" />
              <div>
                <p className="text-sm font-medium text-gray-700">{label}</p>
                <p className="text-xs text-gray-400 font-mono">{settings[field] || '#4f46e5'}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Live Preview */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Live Preview</label>
        <div className="border rounded-lg overflow-hidden">
          <div className="flex h-32">
            <div className="w-48 p-3 text-white text-sm" style={{ backgroundColor: settings.sidebarColor || '#3730a3' }}>
              <div className="font-bold mb-2">Sidebar</div>
              <div className="opacity-70 text-xs space-y-1">
                <div className="bg-white/20 rounded px-2 py-1">Dashboard</div>
                <div className="rounded px-2 py-1">Students</div>
                <div className="rounded px-2 py-1">Teachers</div>
              </div>
            </div>
            <div className="flex-1 bg-gray-50 p-3">
              <div className="flex gap-2 mb-3">
                <button className="text-white text-xs px-3 py-1 rounded" style={{ backgroundColor: settings.primaryColor || '#4f46e5' }}>Primary Button</button>
                <button className="text-white text-xs px-3 py-1 rounded" style={{ backgroundColor: settings.accentColor || '#6366f1' }}>Accent Button</button>
              </div>
              <div className="h-12 rounded" style={{ background: `linear-gradient(to right, ${settings.loginBgFrom || '#312e81'}, ${settings.loginBgTo || '#1e40af'})` }}>
                <span className="text-white text-xs p-2 inline-block">Login Background</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
        Theme changes preview instantly. Click <strong>Save Settings</strong> to apply permanently for all users.
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "school": return renderSchoolInfo();
      case "academic": return renderAcademicYear();
      case "timings": return renderTimings();
      case "attendance": return renderAttendance();
      case "grading": return renderGrading();
      case "fees": return renderFees();
      case "leaves": return renderLeaves();
      case "notifications": return renderNotifications();
      case "uploads": return renderUploads();
      case "theme": return renderTheme();
      default: return null;
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">Settings</h1>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="p-4 md:p-6">
          {message && (
            <div className={`mb-4 p-4 rounded-lg ${message.type === "success" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
              {message.text}
            </div>
          )}

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Tab Sidebar */}
            <div className="lg:w-56 flex-shrink-0">
              <div className="bg-white rounded-lg shadow overflow-hidden">
                {TABS.filter((tab) => !tab.superAdminOnly || user?.role === "super_admin").map((tab) => (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                    className={`w-full text-left px-4 py-3 text-sm font-medium flex items-center gap-2 transition ${
                      activeTab === tab.id ? "bg-indigo-50 text-indigo-700 border-l-4 border-indigo-600" : "text-gray-600 hover:bg-gray-50 border-l-4 border-transparent"
                    }`}>
                    <span>{tab.icon}</span>
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1">
              <div className="bg-white rounded-lg shadow p-6">
                {renderContent()}

                <div className="mt-8 pt-4 border-t flex justify-end">
                  <button onClick={handleSave} disabled={saving}
                    className="bg-indigo-600 text-white px-8 py-2.5 rounded-lg hover:bg-indigo-700 transition font-medium disabled:opacity-50">
                    {saving ? "Saving..." : "Save Settings"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InputField = ({ label, value, onChange, type = "text", ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
      className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" {...props} />
  </div>
);

// Simple add/remove editor for a flat list of strings (e.g. SchoolConfig.feeGroups).
const ListEditor = ({ items, onChange, placeholder }) => {
  const [draft, setDraft] = useState("");

  const addItem = () => {
    const value = draft.trim();
    if (!value || items.includes(value)) return;
    onChange([...items, value]);
    setDraft("");
  };
  const removeItem = (value) => onChange(items.filter((i) => i !== value));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {items.map((item) => (
          <span key={item} className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm">
            {item}
            <button type="button" onClick={() => removeItem(item)} className="text-indigo-400 hover:text-indigo-700">✕</button>
          </span>
        ))}
        {items.length === 0 && <p className="text-sm text-gray-400">No entries yet.</p>}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
          placeholder={placeholder}
          className="flex-1 border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
        <button type="button" onClick={addItem} className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700">Add</button>
      </div>
    </div>
  );
};

const ToggleField = ({ label, description, checked, onChange }) => (
  <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
    <div>
      <p className="text-sm font-medium text-gray-800">{label}</p>
      {description && <p className="text-xs text-gray-500">{description}</p>}
    </div>
    <button type="button" onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? "bg-indigo-600" : "bg-gray-300"}`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  </div>
);

export default AdminSettings;
